import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalApi, type PortalCurrencyStat, type PortalDashboard, type PortalOrder } from '@/api/portal';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { formatPlatformTime } from '@/utils/platformTime';

type CurrencyCode = 'CNY' | 'USDT';

function formatMoney(amount = 0, currency = 'CNY') {
  const symbol = currency === 'USDT' ? '₮' : '¥';
  return `${symbol} ${(amount / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadge(status: string) {
  const map: Record<string, { cls: string; label: string }> = {
    created: { cls: 'tech-badge-warning', label: '待支付' },
    pending: { cls: 'tech-badge-warning', label: '处理中' },
    paid: { cls: 'tech-badge-success', label: '已支付' },
    failed: { cls: 'tech-badge-error', label: '失败' },
    closed: { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: '已关闭' },
    expired: { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: '已过期' },
  };
  const s = map[status] ?? { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: status };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function currencyBadge(currency: string) {
  if (currency === 'USDT') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">USDT</span>;
  }
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">CNY</span>;
}

function payTypeLabel(type: string) {
  const map: Record<string, string> = { alipay: '支付宝', wechat: '微信支付', usdt: 'USDT', bank: '银行卡' };
  return map[type] || type;
}

const emptyStat = (currency: CurrencyCode): PortalCurrencyStat => ({
  currency,
  todayCount: 0,
  todayAmount: 0,
  todayGrossAmount: 0,
  monthCount: 0,
  monthAmount: 0,
  monthGrossAmount: 0,
  totalCount: 0,
  totalAmount: 0,
  totalGrossAmount: 0,
  paidCount: 0,
  pendingCount: 0,
  failedCount: 0,
  availableAmount: 0,
  frozenAmount: 0,
  completedAmount: 0,
});

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PortalDashboard | null>(null);
  const [activeCurrency, setActiveCurrency] = useState<CurrencyCode>('CNY');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi.dashboard()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const cnyStat = useMemo(() => data?.currencyStats?.find((item) => item.currency === 'CNY') || emptyStat('CNY'), [data]);
  const usdtStat = useMemo(() => data?.currencyStats?.find((item) => item.currency === 'USDT') || emptyStat('USDT'), [data]);
  const activeStat = activeCurrency === 'CNY' ? cnyStat : usdtStat;
  const recentOrders = (data?.recentOrders || []).filter((order) => (order.currency || 'CNY') === activeCurrency);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <i className="ri-loader-4-line animate-spin mr-2" />
          加载数据中...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-400">欢迎回来，商户 — 这是您的双币种账户概览</p>
          <button onClick={() => navigate('/transactions')} className="tech-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm self-start sm:self-auto">
            <i className="ri-exchange-line text-sm" />
            查看全部交易
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <BalanceCard currency="CNY" stat={cnyStat} active={activeCurrency === 'CNY'} onClick={() => setActiveCurrency('CNY')} />
          <BalanceCard currency="USDT" stat={usdtStat} active={activeCurrency === 'USDT'} onClick={() => setActiveCurrency('USDT')} />
        </div>

        <div className="flex items-center gap-2">
          <CurrencyButton currency="CNY" active={activeCurrency === 'CNY'} onClick={() => setActiveCurrency('CNY')} />
          <CurrencyButton currency="USDT" active={activeCurrency === 'USDT'} onClick={() => setActiveCurrency('USDT')} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Metric title="本月结算金额" value={formatMoney(activeStat.monthAmount, activeCurrency)} sub={`订单金额 ${formatMoney(activeStat.monthGrossAmount, activeCurrency)} · ${activeStat.monthCount} 笔`} icon={activeCurrency === 'USDT' ? 'ri-currency-line' : 'ri-money-cny-circle-line'} amber={activeCurrency === 'USDT'} />
          <Metric title="今日结算金额" value={formatMoney(activeStat.todayAmount, activeCurrency)} sub={`订单金额 ${formatMoney(activeStat.todayGrossAmount, activeCurrency)} · ${activeStat.todayCount} 笔`} icon="ri-calendar-check-line" />
          <Metric title="待处理订单" value={`${activeStat.pendingCount}`} sub={`失败 ${activeStat.failedCount} 笔`} icon="ri-time-line" />
        </div>

        <div className="tech-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-100">最近交易</h3>
              {currencyBadge(activeCurrency)}
            </div>
            <button onClick={() => navigate('/transactions')} className="text-sm text-blue-400 hover:text-blue-300 font-medium">查看全部</button>
          </div>
          <OrderTable orders={recentOrders} currency={activeCurrency} />
        </div>
      </div>
    </DashboardLayout>
  );
}

function BalanceCard({ currency, stat, active, onClick }: { currency: CurrencyCode; stat: PortalCurrencyStat; active: boolean; onClick: () => void }) {
  const isUsdt = currency === 'USDT';
  return (
    <button onClick={onClick} className={`tech-card p-5 relative overflow-hidden text-left transition-all ${active ? 'tech-border-glow' : ''}`}>
      <div className={`absolute top-0 right-0 px-3 py-1 rounded-bl-lg border-b border-l ${isUsdt ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
        <span className={`text-xs font-medium ${isUsdt ? 'text-amber-400' : 'text-blue-400'}`}>{isUsdt ? '数字资产 USDT' : '法币账户 CNY'}</span>
      </div>
      <div className="flex items-center gap-3 mb-3 mt-2">
        <div className={`w-9 h-9 flex items-center justify-center rounded-lg border ${isUsdt ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
          <i className={isUsdt ? 'ri-currency-line' : 'ri-money-cny-circle-line'} />
        </div>
        <span className="text-sm text-slate-400">可用余额</span>
      </div>
      <p className="text-xl md:text-2xl font-bold text-slate-100 mb-1">{formatMoney(stat.availableAmount, currency)}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
        <span>冻结：{formatMoney(stat.frozenAmount, currency)}</span>
        <span>累计：{formatMoney(stat.totalAmount, currency)}</span>
      </div>
    </button>
  );
}

function CurrencyButton({ currency, active, onClick }: { currency: CurrencyCode; active: boolean; onClick: () => void }) {
  const isUsdt = currency === 'USDT';
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${active ? (isUsdt ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400') : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'}`}>
      <i className={isUsdt ? 'ri-currency-line' : 'ri-money-cny-circle-line'} />
      {isUsdt ? 'USDT 数字资产' : 'CNY 法币'}
    </button>
  );
}

function Metric({ title, value, sub, icon, amber }: { title: string; value: string; sub: string; icon: string; amber?: boolean }) {
  return (
    <div className="tech-card p-4 flex items-center justify-between">
      <div className="min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{title}</p>
        <p className="text-lg font-semibold text-slate-100 truncate">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{sub}</p>
      </div>
      <div className={`w-10 h-10 flex items-center justify-center rounded-full border ${amber ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
        <i className={icon} />
      </div>
    </div>
  );
}

function OrderTable({ orders, currency }: { orders: PortalOrder[]; currency: CurrencyCode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700/80 bg-slate-800/60">
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">平台单号</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">币种</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">支付方式</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">金额</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">手续费</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">结算金额</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">状态</th>
            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">时间</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {orders.length === 0 ? (
            <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">暂无{currency}交易记录</td></tr>
          ) : orders.map((order) => (
            <tr key={order.id} className="tech-table-row">
              <td className="px-5 py-3 text-slate-300 font-mono text-xs">{order.tradeNo}</td>
              <td className="px-5 py-3">{currencyBadge(order.currency || currency)}</td>
              <td className="px-5 py-3 text-slate-400">{payTypeLabel(order.payType)}</td>
              <td className="px-5 py-3 text-green-400 font-medium">{formatMoney(order.amount, order.currency || currency)}</td>
              <td className="px-5 py-3 text-amber-300 font-medium">{formatMoney(order.fee || 0, order.currency || currency)}</td>
              <td className="px-5 py-3 text-cyan-300 font-medium">{formatMoney(order.settleAmount || 0, order.currency || currency)}</td>
              <td className="px-5 py-3">{statusBadge(order.status)}</td>
              <td className="px-5 py-3 text-slate-500 text-xs hidden sm:table-cell">{formatPlatformTime(order.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
